import React, { useState } from "react";
import { PageHero } from "@/components/Section";
import { SEO } from "@/components/SEO";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Newspaper, Send } from "lucide-react";

const empty = { full_name: "", email: "", phone: "", subject: "", message: "", is_press: false, company: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Message envoyé.");
      setForm(empty);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div data-testid="contact-page">
      <SEO title="Contact" description="Contactez le mouvement DEFAR - Les Bâtisseurs : adhésion, presse, partenariats ou toute autre demande." />
      <PageHero overline="Nous écrire" title="Contactez le mouvement" subtitle="Une question, une proposition, une demande presse ? Notre équipe vous répond." />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <h2 className="text-2xl font-bold text-slate-900">Coordonnées</h2>
          <p className="mt-3 text-slate-600">Le siège national du mouvement est à votre écoute.</p>
          <div className="mt-8 space-y-5">
            {[
              { Icon: MapPin, label: "Adresse", value: "Avenue Cheikh Anta Diop, Dakar, Sénégal" },
              { Icon: Phone, label: "Téléphone", value: "+221 77 000 00 00" },
              { Icon: Mail, label: "Email", value: "contact@defar.sn" },
              { Icon: Newspaper, label: "Contact presse", value: "presse@defar.sn" },
            ].map(({ Icon, label, value }) => (
              <div key={label} className="flex items-start gap-4">
                <div className="h-11 w-11 flex items-center justify-center bg-brand-blue text-white shrink-0">
                  <Icon size={20} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">{label}</p>
                  <p className="text-sm text-slate-600">{value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          <form onSubmit={submit} className="bg-white border border-border rounded-sm p-6 sm:p-8 space-y-5">
            <input type="text" value={form.company} onChange={set("company")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Nom complet" value={form.full_name} onChange={set("full_name")} required testid="contact-name" />
              <Field label="Email" type="email" value={form.email} onChange={set("email")} required testid="contact-email" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Téléphone" value={form.phone} onChange={set("phone")} testid="contact-phone" />
              <Field label="Sujet" value={form.subject} onChange={set("subject")} testid="contact-subject" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Message *</label>
              <textarea required value={form.message} onChange={set("message")} rows={5} data-testid="contact-message" className="w-full border border-border px-4 py-2.5 rounded-sm text-sm focus:outline-none focus:border-brand-blue" />
            </div>
            <label className="flex items-center gap-2.5 text-sm text-slate-700">
              <input type="checkbox" checked={form.is_press} onChange={(e) => setForm({ ...form, is_press: e.target.checked })} data-testid="contact-press" className="h-4 w-4 accent-brand-green" />
              Je suis journaliste / demande presse
            </label>
            <button type="submit" disabled={loading} data-testid="contact-submit" className="inline-flex items-center gap-2 bg-brand-green text-white hover:bg-brand-greenhover transition-colors px-7 py-3 font-semibold disabled:opacity-60">
              <Send size={16} /> {loading ? "Envoi..." : "Envoyer le message"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text", required, testid }) => (
  <div>
    <label className="block text-sm font-semibold text-slate-700 mb-1.5">{label}{required && " *"}</label>
    <input type={type} required={required} value={value} onChange={onChange} data-testid={testid} className="w-full border border-border px-4 py-2.5 rounded-sm text-sm focus:outline-none focus:border-brand-blue" />
  </div>
);
